import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, ArrowRight, Info, ChevronRight,
} from "lucide-react";

const PLANS = [
  {
    id: "starter",
    name: "Cricso Starter",
    emoji: "\uD83C\uDFD3",
    tagline: "Perfect for small cricket groups",
    priceMonthly: 0,
    priceYearly: 0,
    yearlySavings: 0,
    maxClubs: 2,
    maxMembersPerClub: 30,
    accent: "from-emerald-500 to-emerald-600",
    accentLight: "bg-emerald-50",
    accentText: "text-emerald-700",
    accentBorder: "border-emerald-200",
    badge: "Free",
    badgeBg: "bg-emerald-100 text-emerald-700",
  },
  {
    id: "league",
    name: "Cricso League",
    emoji: "\uD83E\uDD48",
    tagline: "Ideal for regular cricket organizers",
    priceMonthly: 300,
    priceYearly: 2400,
    yearlySavings: 1200,
    maxClubs: 5,
    maxMembersPerClub: 50,
    accent: "from-blue-500 to-blue-600",
    accentLight: "bg-blue-50",
    accentText: "text-blue-700",
    accentBorder: "border-blue-200",
    badge: "Popular",
    badgeBg: "bg-blue-100 text-blue-700",
  },
  {
    id: "premier",
    name: "Cricso Premier",
    emoji: "\uD83C\uDFC6",
    tagline: "Built for academies, tournaments & businesses",
    priceMonthly: 500,
    priceYearly: 4800,
    yearlySavings: 1200,
    maxClubs: 10,
    maxMembersPerClub: 100,
    accent: "from-amber-500 to-amber-600",
    accentLight: "bg-amber-50",
    accentText: "text-amber-700",
    accentBorder: "border-amber-200",
    badge: "Best Value",
    badgeBg: "bg-amber-100 text-amber-700",
  },
];

const FAQS = [
  {
    q: "Can I cancel anytime?",
    a: "Yes. Your plan stays active until the end of the current billing period. No charges after that.",
  },
  {
    q: "Will my data be lost after downgrade?",
    a: "No. Existing clubs remain accessible, but you can\u2019t create new ones beyond your plan limit.",
  },
  {
    q: "Can I upgrade later?",
    a: "Yes. Upgrades apply immediately with prorated charges for monthly plans.",
  },
  {
    q: "Can I switch between clubs?",
    a: "Yes, instantly. Use the club switcher in the top bar to jump between your clubs.",
  },
  {
    q: "What payment methods are accepted?",
    a: "UPI, Credit/Debit Cards, Net Banking, and Wallets via Razorpay. All secure and GST-compliant.",
  },
  {
    q: "Do you offer refunds?",
    a: "Refunds are handled case-by-case. Contact support within 7 days of purchase.",
  },
];

function fmtINR(n) {
  return "\u20B9" + n.toLocaleString("en-IN");
}

export default function PricingPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [cycle, setCycle] = useState("yearly");

  useEffect(() => {
    const el = document.querySelector(".app-shell");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, []);

  const price = (p) => (cycle === "yearly" ? p.priceYearly : p.priceMonthly);
  const perMonth = (p) =>
    cycle === "yearly" ? Math.round(p.priceYearly / 12) : p.priceMonthly;

  return (
    <div className="app-shell flex flex-col min-h-screen bg-surface">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-surface/80 backdrop-blur-lg border-b border-black/[0.04]">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            onClick={() => nav(-1)}
            className="h-9 w-9 rounded-full hover:bg-black/5 active:bg-black/10 grid place-items-center transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="text-ink" />
          </button>
          <h1 className="text-lg font-bold text-ink">Pricing</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="px-5 pt-8 pb-2 text-center">
          <h2 className="text-[28px] leading-tight font-extrabold text-ink tracking-tight">
            Choose Your Plan
          </h2>
          <p className="text-ink-muted text-[15px] mt-2 max-w-[320px] mx-auto">
            Manage one club or ten — Cricso grows with your game.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center px-5 mt-5 mb-6">
          <div className="bg-black/[0.04] rounded-2xl p-1 flex w-full max-w-[280px]">
            <button
              onClick={() => setCycle("monthly")}
              className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-all duration-200 ${
                cycle === "monthly"
                  ? "bg-white text-ink shadow-sm"
                  : "text-ink-muted"
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle("yearly")}
              className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-all duration-200 relative ${
                cycle === "yearly"
                  ? "bg-white text-ink shadow-sm"
                  : "text-ink-muted"
              }`}
              data-testid="billing-yearly-btn"
            >
              Yearly
              {cycle === "yearly" && (
                <span className="absolute -top-2.5 right-2 bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold animate-in fade-in zoom-in-95 duration-200">
                  Save up to 40%
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="px-4 space-y-3 pb-6">
          {PLANS.map((plan, i) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={i}
              price={price(plan)}
              perMonth={perMonth(plan)}
              cycle={cycle}
              onTap={() => nav(`/pricing/${plan.id}`)}
            />
          ))}
        </div>

        {/* Trust Strip */}
        <div className="px-5 py-4 flex items-center justify-center gap-6 text-xs text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Secure Payments
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Cancel Anytime
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            GST Included
          </span>
        </div>

        {/* FAQ */}
        <div className="px-4 pb-8">
          <h3 className="text-lg font-bold text-ink text-center mb-4">
            Frequently Asked Questions
          </h3>
          <div className="space-y-2">
            {FAQS.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-10 text-center">
          <p className="text-ink-muted text-sm">
            Already have an account?{" "}
            <a
              href="/login"
              className="text-brand font-semibold hover:underline"
            >
              Sign in
            </a>
          </p>
          <p className="text-[11px] text-ink-muted/60 mt-3">
            All prices in INR. GST extra as applicable.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, index, price, perMonth, cycle, onTap }) {
  const isFree = price === 0;

  return (
    <button
      onClick={onTap}
      data-testid={`plan-${plan.id}-card`}
      className={`
        group relative w-full text-left rounded-2xl overflow-hidden
        bg-white border ${plan.accentBorder}
        shadow-[0_1px_3px_rgba(0,0,0,0.04)]
        active:scale-[0.985] active:shadow-sm
        transition-all duration-200 ease-out
        focus:outline-none focus:ring-2 focus:ring-brand/30
      `}
      style={{
        animationDelay: `${index * 60}ms`,
        animation: "slideUp 0.35s ease-out both",
      }}
    >
      {/* Top accent bar */}
      <div
        className={`h-1 w-full bg-gradient-to-r ${plan.accent}`}
      />

      <div className="px-5 py-4">
        {/* Row 1: emoji + name + badge */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl" role="img">
              {plan.emoji}
            </span>
            <span className="text-[17px] font-bold text-ink tracking-tight">
              {plan.name}
            </span>
          </div>
          <span
            className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${plan.badgeBg}`}
          >
            {plan.badge}
          </span>
        </div>

        {/* Row 2: tagline */}
        <p className="text-ink-muted text-[13px] mb-3">{plan.tagline}</p>

        {/* Row 3: price + limits + arrow */}
        <div className="flex items-end justify-between">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="text-[28px] font-extrabold text-ink leading-none tracking-tight">
                {isFree ? "FREE" : fmtINR(price)}
              </span>
              {!isFree && (
                <span className="text-ink-muted text-sm font-medium">
                  /{cycle === "yearly" ? "yr" : "mo"}
                </span>
              )}
            </div>
            {!isFree && cycle === "yearly" && (
              <p className={`text-xs font-medium mt-0.5 ${plan.accentText}`}>
                {fmtINR(perMonth)}/mo • Save {fmtINR(plan.yearlySavings)}/yr
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[11px] font-semibold text-ink-muted bg-black/[0.04] rounded-full px-2 py-0.5">
                {plan.maxClubs} club{plan.maxClubs > 1 ? "s" : ""}
              </span>
              <span className="text-[11px] font-semibold text-ink-muted bg-black/[0.04] rounded-full px-2 py-0.5">
                {plan.maxMembersPerClub} members/club
              </span>
            </div>
          </div>
          <div
            className={`h-9 w-9 rounded-full ${plan.accentLight} grid place-items-center shrink-0 ml-3 group-hover:scale-110 transition-transform duration-200`}
          >
            <ChevronRight size={18} className={plan.accentText} />
          </div>
        </div>
      </div>
    </button>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`rounded-xl border transition-colors duration-200 ${
        open ? "border-black/10 bg-white" : "border-transparent bg-white/60"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3.5 text-left"
        data-testid="faq-question"
      >
        <span className="text-[14px] font-semibold text-ink pr-3">{q}</span>
        <Info
          size={18}
          className={`text-ink-muted shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ease-out ${
          open ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="px-4 pb-3.5 text-[13px] leading-relaxed text-ink-muted">
          {a}
        </p>
      </div>
    </div>
  );
}
